import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  User,
  CreditCard,
  LogOut,
  Settings,
  Tool,
  Users,
  Activity,
  Book,
  Share2,
  Shield,
  Briefcase,
  Key,
} from 'react-feather';
import { useContext } from 'react';
import { UserContext } from '../UserContext';
import { API_BASE_URL } from '../config/api';

// Mirrors Login.js: the same build-time flag that decides whether the
// "Sign in with Microsoft" button renders.
const SSO_ENABLED = process.env.REACT_APP_SSO_ENABLED === 'true';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    logout,
    isPlatformAdmin,
    isOrganizationAdmin,
    isRestrictedOrganizationMember,
    organizationId,
  } = useContext(UserContext);
  const showManagementLinks = !isRestrictedOrganizationMember;
  const showBusinessAssistants = organizationId !== null && organizationId !== undefined;
  const showAdminLink = isPlatformAdmin || isOrganizationAdmin;
  const adminLinkLabel = isPlatformAdmin ? 'Platform Admin' : 'Admin';

  const handleLogout = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        logout();
        navigate('/login');
      } else {
        console.error('Logout failed');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-wrapper">
        <div className="sidebar-header">
          <img src="/images/NexOpta_Logo_Short.png" className="sidebar-logo" alt="Nexopta" />
        </div>
        <nav className="nav flex-column nav-menu">
          <Link 
            className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`} 
            to="/dashboard"
          >
            <Home /> Dashboard
          </Link>
          <Link 
            className={`nav-link ${location.pathname === '/account' ? 'active' : ''}`} 
            to="/account"
          >
            <User /> Account
          </Link>
          {showBusinessAssistants && (
            <Link 
              className={`nav-link ${location.pathname === '/businessassistants' ? 'active' : ''}`} 
              to="/businessassistants"
            >
              <Briefcase /> Business Assistants
            </Link>
          )}

          {showManagementLinks && (
            <>
              <Link 
                className={`nav-link ${location.pathname === '/billing' ? 'active' : ''}`} 
                to="/billing"
              >
                <CreditCard /> Billing
              </Link>
              <Link 
                className={`nav-link ${location.pathname === '/assistants' ? 'active' : ''}`} 
                to="/assistants"
              >
                <Users /> Agents
              </Link>
              <Link 
                className={`nav-link ${location.pathname === '/conversations' ? 'active' : ''}`} 
                to="/conversations"
              >
                <Activity /> Logs
              </Link>
              <Link 
                className={`nav-link ${location.pathname === '/knowledge' ? 'active' : ''}`} 
                to="/knowledge"
              >
                <Book /> Knowledge
              </Link>
              <Link
                className={`nav-link ${location.pathname === '/tools' ? 'active' : ''}`}
                to="/tools"
              >
                <Tool /> Tools
              </Link>

          <Link
            className={`nav-link ${location.pathname === '/guardrails' ? 'active' : ''}`}
            to="/guardrails"
          >
            <Shield /> Guardrails
          </Link>
              <Link
                className={`nav-link ${location.pathname === '/workflow' ? 'active' : ''}`} 
                to="/workflow"
              >
                <Share2 /> AgentOps Studio
              </Link>
            </>
          )}

          {showAdminLink && (
            <Link
              className={`nav-link ${location.pathname === '/admin' ? 'active' : ''}`}
              to="/admin"
            >
              <Settings /> {adminLinkLabel}
            </Link>
          )}

          {/* Admins only, and only once the backend feature flag is on --
              every /api/sso/* route 404s while it is off, so the page would
              have nothing to show. */}
          {showAdminLink && SSO_ENABLED && (
            <Link
              className={`nav-link ${location.pathname === '/settings/sso' ? 'active' : ''}`}
              to="/settings/sso"
            >
              <Key /> Single sign-on
            </Link>
          )}
        </nav>
        <div className="logout-link">
          {/* Inline styles were re-declaring padding (10px 15px) and gap (8px)
              that .nav-link already sets (12px 20px, icon margin 12px). Being
              inline they won, so the Log Out row sat 5px shallower with a
              tighter icon than every nav item above it. The button-specific
              resets now live in .nav-link--button so this row inherits the
              shared nav geometry. */}
          <button
            type="button"
            className="nav-link nav-link--button"
            onClick={handleLogout}
          >
            <LogOut /> Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
